import React, { useState, useEffect } from 'react';
import Seo from '../common/seo';
import NextBatch from '../common/first-believer-fund.js';
import InvestorsIntro from '../common/investors-intro.js';
import RotatingButton from '../bonus/rotating-button.js';
import { useWebsiteInputs } from '../../utils/hooks/useWebsiteInputs';
import { getImageUrl } from '../../utils/supabaseStorage';

function Intro() {
  const { inputs: websiteInputs, loading } = useWebsiteInputs();
  const [isOpenLife, setIsOpenLife] = useState(false); // State for Life On LeapYear
  const [isOpenCommit, setIsOpenCommit] = useState(false); // State for Why 1 Year Commit
  const [isOpenWhyLeapYear, setIsOpenWhyLeapYear] = useState(false); // State for Why LeapYear section
  const [isOpenBuilders, setIsOpenBuilders] = useState(false); // State for the expand section
  const [isOpenFAQ, setIsOpenFAQ] = useState(false); // State for Short Answer FAQ
  const [showCountdown, setShowCountdown] = useState(false); // State for countdown visibility

  const toggleLifeSection = () => {
    setIsOpenLife(!isOpenLife); // Toggle Life On LeapYear section
  };

  const toggleCommitSection = () => {
    setIsOpenCommit(!isOpenCommit); // Toggle Why 1 Year Commit section
  };

  const toggleWhyLeapYearSection = () => {
    setIsOpenWhyLeapYear(!isOpenWhyLeapYear); // Toggle Why LeapYear section
  };

  const toggleBuildersSection = () => {
    setIsOpenBuilders(!isOpenBuilders); // Toggle the section open/close
  };

  const toggleFAQSection = () => {
    setIsOpenFAQ(!isOpenFAQ); // Toggle Short Answer FAQ section
  };

  // Prepare the API data for the button
  const buttonApiData = !loading && websiteInputs?.['Batch Name'] 
    ? `APPLY FOR ${websiteInputs['Batch Name'].toUpperCase()}`
    : 'APPLY FOR FUNDING';

  // Calculate days until deadline and determine display state
  const calculateDeadlineInfo = () => {
    if (!websiteInputs?.['App Deadline']) return { days: null, state: null };
    
    const deadlineDate = new Date(websiteInputs['App Deadline']);
    const currentDate = new Date();
    
    // Reset time to compare only dates
    deadlineDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    const timeDiff = deadlineDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff > 0) {
      return { days: daysDiff, state: 'countdown' };
    } else if (daysDiff === 0) {
      return { days: 0, state: 'today' };
    } else {
      return { days: 0, state: 'past' };
    }
  };

  const deadlineInfo = calculateDeadlineInfo();

  // Synchronize countdown visibility with button rotation timing
  useEffect(() => {
    // Show countdown after 1 second (same timing as button rotation)
    const timer = setTimeout(() => {
      if (!loading && websiteInputs && deadlineInfo.state !== null) {
        setShowCountdown(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [loading, websiteInputs, deadlineInfo.state]);

  return (
    <>
      <Seo path="/">
        <meta name="keywords" content="startups, student founders, seed funding, venture capital, dropouts, students, startups" />
      </Seo>

      <main className="container" role="main">
          <>
            <section className="content-section hero" aria-labelledby="hero-title">
              <h1 className="hero-title wordmark" id="hero-title">LEAPYEAR</h1>
              <div className="hero-content">
                <p>$30,000. For students, dropouts, & grads.</p>
                <p>Each year, we fund 25-30 builders to take 1 year to build something ambitious.</p>
                <p>We fund every 3 months. Anywhere.</p>
                
                <RotatingButton 
                  href="/apply"
                  className="cta-button"
                  role="button"
                  ariaLabel={`Apply for ${websiteInputs?.['Batch Name']?.toUpperCase() || 'Funding'}`}
                  apiData={buttonApiData}
                  isLoading={loading}
                  rotatingTexts={["BE AMBITIOUS..."]}
                />
                
                {deadlineInfo.state !== null && (
                  <div className={`deadline-countdown ${showCountdown ? 'visible' : 'hidden'} ${deadlineInfo.state !== 'countdown' ? 'no-counter' : ''}`}>
                    {deadlineInfo.state === 'countdown' && (
                      <>
                        <span className="countdown-number">{deadlineInfo.days}</span>
                        <span className="countdown-text">day{deadlineInfo.days !== 1 ? 's' : ''} until application deadline</span>
                      </>
                    )}
                    {deadlineInfo.state === 'today' && (
                      <span className="countdown-text today">Application Deadline Today</span>
                    )}
                    {deadlineInfo.state === 'past' && (
                      <span className="countdown-text past">Accepting Applications Now</span>
                    )}
                  </div>
                )}
              </div>
            </section>

            <hr className="light-divider" aria-hidden="true"></hr>

            <NextBatch inContainer={false} />

            <hr className="light-divider" aria-hidden="true"></hr>

            <section className="content-section" aria-labelledby="built-by-title">
              <h2 className="section-title" id="built-by-title">Things Built By People With Little Experience</h2>
              <p className="intro-text">The future is built by the young & ambitious. Always has. Always will.</p>
              <div className="built-by-container" role="list">
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/facebook.png')} alt="Facebook Logo" className="company-logo company-logo--zoom company-logo--zoom-more" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>Harvard Dorm</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/airbnb.png')} alt="Airbnb Logo" className="company-logo" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>RISD Friends</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/youtube.png')} alt="YouTube Logo" className="company-logo" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>Illinois Friends</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/doordash.png')} alt="DoorDash Logo" className="company-logo company-logo--zoom" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>Stanford Class</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/reddit.png')} alt="Reddit Logo" className="company-logo company-logo--zoom" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>UVA Friends</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/google.png')} alt="Google Logo" className="company-logo" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>Stanford PHDs</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/figma.png')} alt="Figma Logo" className="company-logo" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>Brown Friends</span>
                  </div>
                </div>
                <div className="built-by-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/venmo.png')} alt="Venmo Logo" className="company-logo company-logo--zoom" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>UPenn Friends</span>
                  </div>
                </div>
                <div className="built-by-item last-item" role="listitem">
                  <div className="image-container">
                    <img src={getImageUrl('company-logos/twitch.png')} alt="Twitch Logo" className="company-logo" loading="lazy" />
                  </div>
                  <div className="text-container">
                    <span>Yale Friends</span>
                  </div>
                </div>
              </div>
            </section>

            <hr className="light-divider" aria-hidden="true"></hr>

            <section className="content-section" aria-labelledby="bet-title">
              <h2 className="section-title" id="bet-title">
                So This Is A Bet.
              </h2>
              <div className="section-content">
                <p className="intro-text">
                  A bet that if the young & ambitious build for 1 year, great things happen.
                </p>
                <p className="intro-text">
                  We value talent & grit over age. 
                  Builders eager to do the best work of their lives.
                </p>
              </div>
            </section>

            <hr className="light-divider" aria-hidden="true"></hr>

            <section className="content-section" aria-labelledby="investment-title">
              <h2 className="section-title" id="investment-title">Investment.</h2>
              <div className="section-content">
                <p className="text-highlight" aria-label="Investment terms">{websiteInputs?.['Funding']} for {websiteInputs?.['Equity']} Equity</p>
                <p className="intro-text">We invest as a first-believer fund.</p>
                <p className="intro-text">
                  We think a lot can be done with a little. So we write a small check as a first believer.
                </p>
                <p className="intro-text">
                  Our investment is low dilution. This gives founders flexibility to continue their growth however they choose.
                </p>
              </div>
            </section>

            <hr className="light-divider" aria-hidden="true"></hr>

            <InvestorsIntro />

            <hr className="light-divider" aria-hidden="true"></hr>

            <section className="content-section" aria-labelledby="how-it-works-title">
              <h2 className="section-title" id="how-it-works-title">
                How It Works.
              </h2>
              <div className="section-content">
                <p className="intro-text">We tried to think first principles.</p>
                <p className="intro-text">
                  Great builders can come from anywhere, so we invest everywhere.
                </p>
                <p className="intro-text">
                  The internet is filled with great information. So we don't do programming. When you want 1/1 advice, just ask. We aim to help.
                </p>
                <p className="intro-text">
                  Every story is unique. So we invest in anyone - grads, dropouts, & builders still in school. If you want to win, you find the way.
                </p>
                <p className="intro-text">
                  We aim to be your first-believer and increase your odds of success.
                </p>
                <p className="intro-text">
                  Because we invest anywhere, we run on the internet (e.g. video / chat).
                </p>
              </div>
            </section>

            <hr className="light-divider" aria-hidden="true"></hr>
            
            <section className="expand-sections-container">
              <h2 className="extra-section-title" id="additional-info">Additional Info:</h2>

              <section className="expand-section">
                <h3 
                  className="expand-section-title" 
                  onClick={toggleWhyLeapYearSection} 
                  onKeyPress={(e) => e.key === 'Enter' && toggleWhyLeapYearSection()}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpenWhyLeapYear}
                  aria-controls="why-leapyear-content"
                  id="why-leapyear-title"
                >
                  Why LeapYear
                  <span className="expand-icon" aria-hidden="true">{isOpenWhyLeapYear ? '−' : '+'}</span>
                </h3>
                <div 
                  id="why-leapyear-content" 
                  className={`section-content ${isOpenWhyLeapYear ? 'expanded' : ''}`}
                  aria-labelledby="why-leapyear-title"
                  hidden={!isOpenWhyLeapYear}
                >
                  <div className="section-spacer"></div>
                  <ul className="intro-list">
                    <li>
                      <strong>Your First Investor</strong> 
                      <ul>
                        <li>Startups are built on momentum. We invest early, wherever you are</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Flexibility</strong> 
                      <ul>
                        <li>We don't make you move or drop out. If you commit, we will fund</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Support When You Want It</strong> 
                      <ul>
                        <li>We offer 1/1 advice when you want it. It's your business though so we advise, not dictate</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Top 1% Network</strong> 
                      <ul>
                        <li>LY taps you into a wider network to increase your odds of success</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="expand-section">
                <h3 
                  className="expand-section-title" 
                  onClick={toggleCommitSection}
                  onKeyPress={(e) => e.key === 'Enter' && toggleCommitSection()}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpenCommit}
                  aria-controls="commit-content"
                  id="commit-title"
                >
                  Why 1 Year Commit
                  <span className="expand-icon" aria-hidden="true">{isOpenCommit ? '−' : '+'}</span>
                </h3>
                <div 
                  id="commit-content"
                  className={`section-content ${isOpenCommit ? 'expanded' : ''}`}
                  aria-labelledby="commit-title"
                  hidden={!isOpenCommit}
                >
                  <div className="section-spacer"></div>
                  <p className="intro-text">Great things take time to build. We invest in builders, not ideas</p>
                  <p className="intro-text">There will be many ups and downs. It will be hard. Hard things are hard</p>
                  <p className="intro-text">But if you are relentless. We believe you will find a way to succeed</p>
                  <p className="intro-text">Our ask though, is you commit to building for 1 year minimum</p>
                  <p className="intro-text">It takes 6-12 months to get to the frontier of an industry. That is where the opportunity is</p>
                  <p className="intro-text">So perhaps the first idea might not work. Perhaps you might try 3 ideas</p>
                  <p className="intro-text">Just keep building, talking to customers, & getting 1% better daily</p>
                </div>
              </section>

              <section className="expand-section">
                <h3 
                  className="expand-section-title" 
                  onClick={toggleLifeSection}
                  onKeyPress={(e) => e.key === 'Enter' && toggleLifeSection()}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpenLife}
                  aria-controls="life-content"
                  id="life-title"
                >
                  Life On LeapYear
                  <span className="expand-icon" aria-hidden="true">{isOpenLife ? '−' : '+'}</span>
                </h3>
                <div 
                  id="life-content"
                  className={`section-content ${isOpenLife ? 'expanded' : ''}`}
                  aria-labelledby="life-title"
                  hidden={!isOpenLife}
                >
                  <div className="section-spacer"></div>
                  <p className="intro-text">Talk to users. Understand their problems. Build solutions</p>
                  <p className="intro-text">Work hard. The relentless will succeed</p>
                  <p className="intro-text">We will check in with you if you want help but all optional</p>
                  <p className="intro-text">Thats it. Just go build</p>
                </div>
              </section>

              <section className="expand-section">
                <h3 
                  className="expand-section-title" 
                  onClick={toggleFAQSection}
                  onKeyPress={(e) => e.key === 'Enter' && toggleFAQSection()}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpenFAQ}
                  aria-controls="faq-content"
                  id="faq-title"
                >
                  Short Answer FAQ
                  <span className="expand-icon" aria-hidden="true">{isOpenFAQ ? '−' : '+'}</span>
                </h3>
                <div 
                  id="faq-content"
                  className={`section-content ${isOpenFAQ ? 'expanded' : ''}`}
                  aria-labelledby="faq-title"
                  hidden={!isOpenFAQ}
                >
                  <div className="section-spacer"></div>

                  <ul className="intro-list">
                    <li>
                      <strong>How Does It Work?</strong> 
                      <ul>
                        <li>Teams apply. We fund a few. They build. We offer help</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Is it Remote / Virtual?</strong> 
                      <ul>
                        <li>Yes. Allows us to invest anywhere</li>
                      </ul>
                    </li>
                    <li>
                      <strong>What Is Required?</strong> 
                      <ul>
                        <li>Commit. Talk to customers. Build something ambitious</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Is This An Accelerator?</strong> 
                      <ul>
                        <li>No. LeapYear is a 1-year commit. Not 3 mths. Great things take time</li>
                      </ul>
                    </li>
                    <li>
                      <strong>What Happens After 1 Year?</strong> 
                      <ul>
                        <li>Ideally, your work is scaling. Maybe it doesn't but you learn</li>
                      </ul>
                    </li>
                    <li>
                      <strong>What Stage Do You Invest At?</strong> 
                      <ul>
                        <li>Day 1. That's why we are willing to back teams still in school</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Do I Need to Still Be In School?</strong> 
                      <ul>
                        <li>No. We review all applications. We've funded many grads & dropouts</li>
                      </ul>
                    </li>
                    <li>
                      <strong>If I've Already Taken Funding, Can I Still Apply?</strong> 
                      <ul>
                        <li>Yes</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="expand-section">
                <h3 
                  className="expand-section-title" 
                  onClick={toggleBuildersSection}
                  onKeyPress={(e) => e.key === 'Enter' && toggleBuildersSection()}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpenBuilders}
                  aria-controls="builders-content"
                  id="builders-title"
                >
                  Past Investments ({websiteInputs?.['Total Investments']}):
                  <span className="expand-icon" aria-hidden="true">{isOpenBuilders ? '−' : '+'}</span>
                </h3>
                <div 
                  id="builders-content"
                  className={`section-content ${isOpenBuilders ? 'expanded' : ''}`}
                  aria-labelledby="builders-title"
                  hidden={!isOpenBuilders}
                >
                  <div className="section-spacer"></div> 
                  <p className="intro-text">We back builders with a unique perspective, regardless of background</p>
                  <p className="intro-text">We will fund teams in any industry</p>
                  <p className="intro-text">To date, we've funded software, hardware, robotics, bio, medtech + CPG</p>
                  <div className="faq-button-container">
                    <a href="/investments" className="read-faq-button">
                      See Past Investments
                    </a>
                  </div>
                </div>
              </section>
            </section>
          </>
      </main>
    </>
  );
}

export default Intro;